import { useEffect, useState } from 'react'
import { useSearchParams, Link, Navigate } from 'react-router-dom'
import { useEvents } from '../hooks/useEvents'
import { useRoster } from '../hooks/useRoster'
import { useVolunteerOffers, useVolunteerCall, useSetVolunteerCall, useCloseVolunteerCall, useConfirmVolunteer } from '../hooks/useVolunteerLoop'
import { useSetRoleDefaults } from '../hooks/useRoles'
import { useEventBriefings } from '../hooks/useBriefings'
import { useMe } from '../hooks/useMe'
import { EvIcon as Icon } from '../components/volunteer/EvIcon'
import {
  EV, EvCard, EvAvatar, EvBadge, StatusDot, toneOf, toneForRole,
  fmtDayChip, fmtDateRange, initials, primaryBtn, ghostBtn
} from '../components/volunteer/evKit'
import type { EventListItem, LibraryRole, RosterAssignment, VolunteerOffer, VolunteerCall } from '../types'

// Volunteers — the new top-level manager section (BUILD_PLAN PR E-3). Scoped to
// a chosen event (selector defaults to the nearest upcoming event, or ?event=).
// Three sub-tabs: Overview (mini-stats + Send-call composer + offers summary),
// Directory (searchable cards + per-volunteer record drawer), Briefing acks
// (per-briefing % + volunteer × briefing matrix). Onboarding lives in InfoHub.

const V_TABS = [
  { key: 'overview', label: 'Overview', icon: 'grid' },
  { key: 'directory', label: 'Directory', icon: 'users' },
  { key: 'acks', label: 'Briefing acks', icon: 'shieldCheck' }
] as const
type VTab = typeof V_TABS[number]['key']

interface VolLite {
  id: number
  name: string | null
  canDo: number[]
  phone?: string | null
  email?: string | null
  ecName?: string | null
  ecPhone?: string | null
  sinceYear?: number | null
  priorEvents?: number
  offer?: VolunteerOffer | null
}

// VolunteersPanel — the event-scoped body, now rendered as a per-event tab
// inside EventDetail (Planning group). Admin-gated (defence in depth; the tab is
// also admin-only at registration). The three sub-tabs are unchanged.
export function VolunteersPanel({ event }: { event: EventListItem }) {
  const { data: me } = useMe()
  const [tab, setTab] = useState<VTab>('overview')

  if (!me?.isAdmin) {
    return <div className="text-sm text-ink-soft">The Volunteers section is available to event admins.</div>
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-soft max-w-2xl">
        Onboarding now happens in InfoHub. Here you review who's put themselves forward, keep the directory, and track briefings.
      </p>

      {/* sub-tabs */}
      <div className="border-b" style={{ borderColor: EV.line }}>
        <div className="flex gap-1.5">
          {V_TABS.map((t) => {
            const on = t.key === tab
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] cursor-pointer"
                style={{ borderRadius: '8px 8px 0 0', border: 'none', borderBottom: on ? `2.5px solid ${EV.green}` : '2.5px solid transparent', background: on ? EV.parchment : 'transparent', color: on ? EV.green : EV.inkSoft, fontWeight: on ? 800 : 600 }}
              >
                <Icon name={t.icon} size={15} color={on ? EV.green : EV.inkSoft} strokeWidth={on ? 2.1 : 1.8} /> {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {tab === 'overview' ? (
        <OverviewTab event={event} />
      ) : tab === 'directory' ? (
        <DirectoryTab event={event} />
      ) : (
        <AcksTab eventId={event.id} />
      )}
    </div>
  )
}

// Volunteers is now a per-event tab; the old /volunteers page just forwards to
// the event's Volunteers tab (preferring ?event=, else the nearest upcoming
// event) so bookmarks and the sidebar-era links keep working.
export function Volunteers() {
  const [searchParams] = useSearchParams()
  const eventsQ = useEvents()

  if (!eventsQ.data) return null
  const events = eventsQ.data
  const todayIso = new Date().toISOString().slice(0, 10)
  const sorted = [...events].sort((a, b) => a.startDate.localeCompare(b.startDate))
  const nearest = sorted.find((e) => e.endDate >= todayIso) ?? sorted[sorted.length - 1]
  const paramId = Number(searchParams.get('event'))
  const targetId = Number.isFinite(paramId) && paramId > 0 ? paramId : nearest?.id
  return <Navigate to={targetId ? `/events/${targetId}?tab=volunteers` : '/events'} replace />
}

// ---- shared: assemble the per-event volunteer set -------------------------
function useVolunteers(eventId: number) {
  const rosterQ = useRoster(eventId)
  const offersQ = useVolunteerOffers(eventId)
  const offers = offersQ.data ?? []
  const assignments = rosterQ.data?.assignments ?? []
  const map = new Map<number, VolLite>()
  for (const o of offers) {
    map.set(o.userId, {
      id: o.userId, name: o.volunteer.name, canDo: o.roleIds,
      phone: o.volunteer.phone, email: o.volunteer.email,
      ecName: o.volunteer.emergencyContactName, ecPhone: o.volunteer.emergencyContactPhone,
      sinceYear: o.volunteer.sinceYear, priorEvents: o.volunteer.priorEvents, offer: o
    })
  }
  for (const a of assignments) {
    if (!map.has(a.userId)) map.set(a.userId, { id: a.userId, name: a.userName, canDo: [] })
  }
  return {
    volunteers: Array.from(map.values()),
    vol: (id: number) => map.get(id) ?? { id, name: '?', canDo: [] },
    roles: rosterQ.data?.roles ?? [],
    assignments,
    offers,
    forVol: (id: number) => assignments.filter((a) => a.userId === id),
    loading: rosterQ.isLoading || offersQ.isLoading
  }
}

// ===========================================================================
// OVERVIEW
// ===========================================================================
function OverviewTab({ event }: { event: EventListItem }) {
  const { volunteers, assignments, offers, roles } = useVolunteers(event.id)
  const callQ = useVolunteerCall(event.id)
  const pending = offers.filter((o) => o.status === 'pending')
  const placed = new Set(assignments.map((a) => a.userId)).size
  const confirmed = assignments.filter((a) => a.status === 'confirmed').length

  return (
    <div style={{ maxWidth: 1000, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <MiniStat value={String(volunteers.length)} label="In directory" />
        <MiniStat value={String(pending.length)} label="Offers to review" accent={pending.length ? EV.amber : undefined} />
        <MiniStat value={String(placed)} label="People placed" />
        <MiniStat value={String(confirmed)} label="Duties confirmed" accent={EV.live} />
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)' }}>
        <SendCallComposer event={event} roles={roles} call={callQ.data} />
        <EvCard style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: EV.serif, fontSize: 16, fontWeight: 700 }}>Offers from InfoHub</div>
            {pending.length > 0 && <span style={{ fontFamily: EV.body, fontSize: 12, fontWeight: 800, color: '#fff', background: EV.amber, borderRadius: 999, padding: '1px 9px' }}>{pending.length} new</span>}
          </div>
          <div style={{ fontFamily: EV.body, fontSize: 12.5, color: EV.inkSoft, marginTop: 3 }}>Volunteers responding to the call. Review and place them on the roster.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 12 }}>
            {pending.slice(0, 5).map((o) => (
              <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderTop: `1px solid ${EV.line}` }}>
                <EvAvatar name={o.volunteer.name} size={28} />
                <span style={{ fontFamily: EV.body, fontSize: 13, fontWeight: 700 }}>{o.volunteer.name}</span>
                <span style={{ fontFamily: EV.body, fontSize: 11.5, color: EV.inkSoft }}>{o.mode === 'whole' ? 'all weekend' : `${o.days.length} day(s)`}</span>
                <span style={{ flex: 1 }} /><Icon name="chevron" size={14} color={EV.inkSoft} />
              </div>
            ))}
            {pending.length === 0 && <div style={{ fontFamily: EV.body, fontSize: 12.5, fontStyle: 'italic', color: EV.inkSoft, padding: '8px 0' }}>No offers waiting.</div>}
          </div>
          <Link to={`/events/${event.id}?tab=roster`} style={{ ...primaryBtn(), marginTop: 12, width: '100%', justifyContent: 'center', textDecoration: 'none' }}>
            Review &amp; place offers <Icon name="arrowRight" size={15} color="#fff" />
          </Link>
        </EvCard>
      </div>
    </div>
  )
}

function SendCallComposer({ event, roles, call }: { event: EventListItem; roles: LibraryRole[]; call: VolunteerCall | undefined }) {
  const setCall = useSetVolunteerCall(event.id)
  const closeCall = useCloseVolunteerCall(event.id)
  const setDefaults = useSetRoleDefaults()
  const live = !!call?.isActive
  const defaultMsg = `We need your help to run ${event.name} — pick the days and roles you can do.`
  const [roleSel, setRoleSel] = useState<Set<number> | null>(null)
  const [msg, setMsg] = useState<string>('')

  useEffect(() => {
    // Seed once roles + call have loaded. An existing call wins; otherwise
    // pre-tick the default set (Roles.is_default) so we don't select every
    // role by default (item 1). Fall back to all roles if no defaults are set.
    if (roleSel === null && roles.length > 0) {
      const defaultIds = roles.filter((r) => r.isDefault).map((r) => r.id)
      const seed = call?.roleIds && call.roleIds.length > 0
        ? call.roleIds
        : (defaultIds.length > 0 ? defaultIds : roles.map((r) => r.id))
      setRoleSel(new Set(seed))
      setMsg(call?.message ?? defaultMsg)
    }
  }, [roles, call, roleSel, defaultMsg])

  const sel = roleSel ?? new Set(roles.map((r) => r.id))
  const toggle = (id: number) => setRoleSel((prev) => { const n = new Set(prev ?? sel); n.has(id) ? n.delete(id) : n.add(id); return n })
  const publish = () => setCall.mutate({ message: msg.trim() || null, audience: null, roleIds: Array.from(sel), active: true })

  return (
    <EvCard style={{ padding: 16, borderColor: live ? EV.live : EV.line }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 36, height: 36, borderRadius: 9, background: EV.greenSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="send" size={18} color={EV.green} /></span>
        <div style={{ flex: 1 }}><div style={{ fontFamily: EV.serif, fontSize: 16, fontWeight: 700 }}>Call for volunteers</div><div style={{ fontFamily: EV.body, fontSize: 12, color: EV.inkSoft }}>Publishes the flash banner on every InfoHub home screen.</div></div>
        {live && <EvBadge tone="green" solid>Live now</EvBadge>}
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <FieldLabel>Roles to call for</FieldLabel>
          {roles.length > 0 && (
            <button
              onClick={() => setDefaults.mutate(Array.from(sel))}
              disabled={setDefaults.isPending || sel.size === 0}
              title="Make the ticked roles the default set — pre-selected on future events and calls"
              style={{ ...ghostBtn(), fontSize: 11, padding: '4px 9px', opacity: setDefaults.isPending || sel.size === 0 ? 0.5 : 1 }}
            >
              {setDefaults.isPending ? 'Saving…' : setDefaults.isSuccess ? '✓ Default set saved' : 'Set as default set'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
          {roles.length === 0 && <span style={{ fontFamily: EV.body, fontSize: 12, fontStyle: 'italic', color: EV.inkSoft }}>No roles configured on this event yet — set them up on the Roster tab.</span>}
          {roles.map((r) => { const on = sel.has(r.id); const t = toneOf(r); return (
            <button key={r.id} onClick={() => toggle(r.id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, border: `1.5px solid ${on ? t.dot : EV.line}`, background: on ? t.bg : '#fff', color: on ? t.fg : EV.inkSoft, fontFamily: EV.body, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {on && <Icon name="check" size={12} color={t.fg} strokeWidth={2.6} />}{r.name}
            </button>
          ) })}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <FieldLabel>Message</FieldLabel>
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} style={{ width: '100%', marginTop: 7, minHeight: 70, resize: 'none', borderRadius: 9, border: `1px solid ${EV.line}`, padding: '10px 12px', fontFamily: EV.body, fontSize: 13, color: EV.ink, outline: 'none' }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, padding: '9px 11px', background: EV.parchment, borderRadius: 8 }}>
        <Icon name="info" size={15} color={EV.inkSoft} />
        <span style={{ fontFamily: EV.body, fontSize: 11.5, color: EV.inkSoft }}>Shows on InfoHub home for all volunteers · {fmtDateRange([event.startDate, event.endDate])} · {sel.size} role{sel.size === 1 ? '' : 's'}</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={publish} disabled={setCall.isPending || sel.size === 0} style={{ ...primaryBtn(), flex: 1, justifyContent: 'center', padding: 11, opacity: setCall.isPending || sel.size === 0 ? 0.6 : 1 }}>
          <Icon name="send" size={15} color="#fff" /> {setCall.isPending ? 'Publishing…' : live ? 'Re-send call' : 'Publish to InfoHub'}
        </button>
        {live && <button onClick={() => closeCall.mutate()} disabled={closeCall.isPending} style={{ ...ghostBtn(), padding: '11px 14px' }}>Close call</button>}
      </div>
      {(setCall.error || closeCall.error) && <div style={{ marginTop: 8, fontFamily: EV.body, fontSize: 12, color: EV.oxblood }}>{((setCall.error || closeCall.error) as Error).message}</div>}
    </EvCard>
  )
}

// ===========================================================================
// DIRECTORY
// ===========================================================================
function DirectoryTab({ event }: { event: EventListItem }) {
  const { volunteers, vol, forVol, roles } = useVolunteers(event.id)
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<number | null>(null)
  const list = volunteers.filter((v) => (v.name ?? '').toLowerCase().includes(q.toLowerCase()))

  const statusOf = (v: VolLite): { t: string; tone: 'green' | 'amber' | 'slate' } => {
    const a = forVol(v.id)
    if (a.some((x) => x.status === 'confirmed')) return { t: 'Confirmed', tone: 'green' }
    if (a.length) return { t: 'Assigned', tone: 'amber' }
    if (v.offer) return { t: 'Available', tone: 'slate' }
    return { t: '—', tone: 'slate' }
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search volunteers…" style={{ width: '100%', padding: '9px 12px 9px 34px', borderRadius: 8, border: `1px solid ${EV.line}`, fontFamily: EV.body, fontSize: 13, outline: 'none' }} />
          <span style={{ position: 'absolute', left: 11, top: 10 }}><Icon name="filter" size={15} color={EV.inkSoft} /></span>
        </div>
        <span style={{ fontFamily: EV.body, fontSize: 12.5, color: EV.inkSoft }}>{list.length} volunteers</span>
      </div>
      {volunteers.length === 0 && <div style={{ fontFamily: EV.body, fontSize: 13, fontStyle: 'italic', color: EV.inkSoft }}>No volunteers involved with this event yet. Publish a call and approve offers to populate the directory.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {list.map((v) => { const st = statusOf(v); return (
          <button key={v.id} onClick={() => setSel(v.id)} style={{ textAlign: 'left', background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 11, padding: 14, cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <EvAvatar name={v.name} size={44} ring={st.tone === 'green' ? EV.live : st.tone === 'amber' ? EV.amber : undefined} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'space-between' }}>
                <span style={{ fontFamily: EV.serif, fontSize: 15.5, fontWeight: 700 }}>{v.name}</span>
                <EvBadge tone={st.tone} solid={st.tone === 'green'}>{st.t}</EvBadge>
              </div>
              <div style={{ fontFamily: EV.body, fontSize: 11.5, color: EV.inkSoft, marginTop: 2 }}>{v.priorEvents != null ? `${v.priorEvents} events` : ''}{v.sinceYear ? ` · since ${v.sinceYear}` : ''}</div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
                {v.canDo.map((rid) => { const r = roles.find((x) => x.id === rid); return <span key={rid} title={r?.name} style={{ width: 9, height: 9, borderRadius: '50%', background: r ? toneOf(r).dot : EV.slate }} /> })}
              </div>
            </div>
          </button>
        ) })}
      </div>
      {sel != null && <RecordDrawer event={event} v={vol(sel)} duties={forVol(sel)} onClose={() => setSel(null)} />}
    </div>
  )
}

function RecordDrawer({ event, v, duties, onClose }: { event: EventListItem; v: VolLite; duties: RosterAssignment[]; onClose: () => void }) {
  const confirmVol = useConfirmVolunteer(event.id)
  const { roles } = useVolunteers(event.id)
  const roleName = (rid: number) => roles.find((r) => r.id === rid)?.name ?? `#${rid}`
  const offer = v.offer
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,26,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} className="el-scroll" style={{ width: 460, maxWidth: '92vw', height: '100%', background: EV.parchment, overflowY: 'auto', boxShadow: '-20px 0 60px rgba(0,0,0,0.25)' }}>
        <div style={{ background: EV.green, color: '#fff', padding: '20px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: EV.brassBright, color: EV.green, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: EV.body, fontWeight: 800, fontSize: 20 }}>{initials(v.name)}</span>
            <div style={{ flex: 1 }}><div style={{ fontFamily: EV.serif, fontSize: 23, fontWeight: 700 }}>{v.name}</div><div style={{ fontFamily: EV.body, fontSize: 12.5, color: 'rgba(255,255,255,0.8)' }}>Volunteer{v.priorEvents != null ? ` · ${v.priorEvents} events` : ''}{v.sinceYear ? ` since ${v.sinceYear}` : ''}</div></div>
            <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Icon name="x" size={17} color="#fff" /></button>
          </div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <RecBlock title="Contact">
            <RecRow icon="phone" label="Mobile" value={v.phone ?? '—'} />
            <RecRow icon="message" label="Email" value={v.email ?? '—'} />
          </RecBlock>
          {(v.ecName || v.ecPhone) && (
            <RecBlock title="Emergency contact">
              <RecRow icon="user" label={v.ecName ?? 'Contact'} value={v.ecPhone ?? '—'} />
            </RecBlock>
          )}
          {v.canDo.length > 0 && (
            <RecBlock title="Roles they offered">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{v.canDo.map((rid) => { const r = roles.find((x) => x.id === rid); return <EvBadge key={rid} tone={r ? toneForRole(r) : 'slate'}>{roleName(rid)}</EvBadge> })}</div>
            </RecBlock>
          )}
          <RecBlock title={`Availability — ${event.name}`}>
            {offer ? (
              <div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>{(offer.mode === 'whole' ? offerWholeDays(event) : offer.days).map((d) => <EvBadge key={d} tone="slate">{fmtDayChip(d)}</EvBadge>)}</div>
                <div style={{ fontFamily: EV.body, fontSize: 12, color: EV.inkSoft }}>Offered via InfoHub · status <strong style={{ color: offer.status === 'approved' ? EV.live : EV.inkSoft }}>{offer.status}</strong></div>
              </div>
            ) : <span style={{ fontFamily: EV.body, fontSize: 12.5, fontStyle: 'italic', color: EV.inkSoft }}>Hasn't responded to the call for this event.</span>}
          </RecBlock>
          <RecBlock title="This event's duties">
            {duties.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[...duties].sort((a, b) => a.assignmentDate.localeCompare(b.assignmentDate)).map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <StatusDot status={a.status} />
                    <span style={{ fontFamily: EV.body, fontSize: 13, fontWeight: 700 }}>{roleName(a.roleId)}</span>
                    <span style={{ fontFamily: EV.body, fontSize: 11.5, color: EV.inkSoft }}>{fmtDayChip(a.assignmentDate)}</span>
                  </div>
                ))}
                {duties.some((a) => a.status === 'assigned') && <button onClick={() => confirmVol.mutate(v.id)} style={{ ...primaryBtn(), alignSelf: 'flex-start', marginTop: 4 }}><Icon name="check" size={13} color="#fff" strokeWidth={2.4} /> Confirm all duties</button>}
              </div>
            ) : <span style={{ fontFamily: EV.body, fontSize: 12.5, fontStyle: 'italic', color: EV.inkSoft }}>Not placed on this event yet.</span>}
          </RecBlock>
        </div>
      </div>
    </div>
  )
}

function offerWholeDays(event: EventListItem): string[] {
  const out: string[] = []
  const start = new Date(event.startDate + 'T00:00:00Z'); const end = new Date(event.endDate + 'T00:00:00Z')
  const c = new Date(start); let n = 0
  while (c.getTime() <= end.getTime() && n < 365) { out.push(c.toISOString().slice(0, 10)); c.setUTCDate(c.getUTCDate() + 1); n++ }
  return out
}

function RecBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><div style={{ fontFamily: EV.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: EV.inkSoft, marginBottom: 8 }}>{title}</div><EvCard style={{ padding: 13 }}>{children}</EvCard></div>
}
function RecRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0' }}><Icon name={icon} size={15} color={EV.brass} /><span style={{ fontFamily: EV.body, fontSize: 13, fontWeight: 700, flex: 1 }}>{label}</span><span style={{ fontFamily: EV.mono, fontSize: 12.5, color: EV.inkSoft }}>{value}</span></div>
}

// ===========================================================================
// BRIEFING ACKS
// ===========================================================================
function AcksTab({ eventId }: { eventId: number }) {
  const briefingsQ = useEventBriefings(eventId)
  const data = briefingsQ.data
  if (briefingsQ.isLoading) return <div className="text-sm font-mono text-ink-soft py-4">Loading briefings…</div>
  if (!data) return null
  const topics = data.topics
  const people = [...data.rostered].sort((a, b) => (a.userName ?? '').localeCompare(b.userName ?? ''))

  const ackState = (userId: number, topic: typeof topics[number]): 'acked' | 'stale' | 'none' => {
    const a = topic.acks.find((x) => x.userId === userId)
    if (!a) return 'none'
    return a.isStale ? 'stale' : 'acked'
  }
  const pct = (topic: typeof topics[number]) => {
    if (people.length === 0) return 0
    const acked = people.filter((p) => ackState(p.userId, topic) === 'acked').length
    return Math.round((acked / people.length) * 100)
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <div><h3 style={{ fontFamily: EV.serif, fontSize: 17, fontWeight: 700, margin: 0 }}>Briefing &amp; duties acknowledgements</h3>
        <div style={{ fontFamily: EV.body, fontSize: 12.5, color: EV.inkSoft, marginTop: 3 }}>Who's read each briefing on InfoHub. Stale = acknowledged an older version since updated.</div></div>

      {topics.length === 0 && <div style={{ marginTop: 14, fontFamily: EV.body, fontSize: 13, fontStyle: 'italic', color: EV.inkSoft }}>No briefings added to this event yet — add topics on the event's Briefings tab.</div>}

      <div style={{ display: 'flex', gap: 12, margin: '14px 0', flexWrap: 'wrap' }}>
        {topics.map((b) => (
          <div key={b.id} style={{ flex: 1, minWidth: 200, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontFamily: EV.body, fontSize: 13, fontWeight: 800 }}>{b.title}</div>
            <div style={{ fontFamily: EV.mono, fontSize: 10.5, color: EV.inkSoft, margin: '3px 0 8px' }}>v{b.version}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ flex: 1, height: 6, background: EV.parchmentDk, borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${pct(b)}%`, height: '100%', background: pct(b) === 100 ? EV.live : EV.amber, borderRadius: 999 }} /></div>
              <span style={{ fontFamily: EV.mono, fontSize: 12, fontWeight: 700, color: pct(b) === 100 ? EV.live : EV.inkSoft }}>{pct(b)}%</span>
            </div>
          </div>
        ))}
      </div>

      {topics.length > 0 && (
        <EvCard style={{ overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `1.4fr repeat(${topics.length}, 1fr)`, background: EV.parchment, borderBottom: `1px solid ${EV.line}` }}>
            <Cellh>Volunteer</Cellh>
            {topics.map((b) => <Cellh key={b.id} center>{b.title.replace(/—.*/, '').trim()}</Cellh>)}
          </div>
          {people.map((p, i) => (
            <div key={p.userId} style={{ display: 'grid', gridTemplateColumns: `1.4fr repeat(${topics.length}, 1fr)`, borderBottom: i === people.length - 1 ? 'none' : `1px solid ${EV.line}`, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px' }}><EvAvatar name={p.userName} size={28} /><span style={{ fontFamily: EV.body, fontSize: 13, fontWeight: 700 }}>{p.userName}</span></div>
              {topics.map((b) => { const st = ackState(p.userId, b); return (
                <div key={b.id} style={{ display: 'flex', justifyContent: 'center', padding: '9px 8px' }}>
                  {st === 'acked' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: EV.body, fontSize: 11, fontWeight: 800, color: EV.live }}><Icon name="check" size={13} color={EV.live} strokeWidth={2.6} /> Read</span>}
                  {st === 'stale' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: EV.body, fontSize: 11, fontWeight: 800, color: '#9a5a1e' }}><Icon name="alert" size={12} color="#9a5a1e" /> Stale</span>}
                  {st === 'none' && <span style={{ fontFamily: EV.body, fontSize: 11, fontWeight: 700, color: '#c3bdb0' }}>—</span>}
                </div>
              ) })}
            </div>
          ))}
          {people.length === 0 && <div style={{ padding: 18, fontFamily: EV.body, fontSize: 13, fontStyle: 'italic', color: EV.inkSoft }}>No one placed on this event yet.</div>}
        </EvCard>
      )}
    </div>
  )
}

function Cellh({ children, center }: { children: React.ReactNode; center?: boolean }) {
  return <div style={{ fontFamily: EV.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: EV.inkSoft, padding: '10px 14px', textAlign: center ? 'center' : 'left' }}>{children}</div>
}
function MiniStat({ value, label, accent }: { value: string; label: string; accent?: string }) {
  return <div style={{ flex: 1, minWidth: 130, background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 10, padding: '13px 15px' }}><div style={{ fontFamily: EV.serif, fontSize: 26, fontWeight: 800, color: accent || EV.ink, lineHeight: 1 }}>{value}</div><div style={{ fontFamily: EV.body, fontSize: 11, fontWeight: 700, color: EV.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 5 }}>{label}</div></div>
}
function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontFamily: EV.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: EV.inkSoft }}>{children}</div>
}
